import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, isMfaRequired, ApiError } from '@teivor/shared-utils/api';

import { Button, Input, FormField, PasswordInput, Switch } from '../../lib/ui';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  remember: z.boolean().optional(),
});

type Values = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = React.useState<string | null>(null);
  const [locked, setLocked] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<Values>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (values: Values) => {
    setError(null);
    setLocked(false);
    setSubmitting(true);
    try {
      const result = await api.iam.login({ email: values.email, password: values.password });
      if (isMfaRequired(result)) {
        navigate(`/auth/mfa?challengeId=${encodeURIComponent(result.challengeId)}`);
        return;
      }
      navigate(params.get('return') || '/');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 423 || err.code === 'ACCOUNT_LOCKED') {
          setLocked(true);
          return;
        }
        if (err.status === 401) {
          setError(t('auth.invalid_credentials'));
          return;
        }
      }
      setError(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="mb-1 text-2xl font-semibold">{t('auth.sign_in')}</h1>
      <p className="mb-6 text-sm text-[var(--color-fg-secondary)]">{t('client.subtitle')}</p>

      {locked ? (
        <div role="alert" className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-danger)] bg-[var(--color-surface-2)] p-3 text-sm text-[var(--color-danger)]">
          <strong>{t('auth.account_locked')}.</strong> {t('auth.account_locked_msg', { minutes: 15 })}
        </div>
      ) : null}
      {error ? (
        <div role="alert" className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-danger)] bg-[var(--color-surface-2)] p-3 text-sm text-[var(--color-danger)]">
          {error}
        </div>
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <FormField label={t('auth.email')} htmlFor="email" required error={errors.email ? t('auth.email') : undefined}>
          <Input id="email" type="email" autoComplete="email" invalid={!!errors.email} {...register('email', { required: true })} />
        </FormField>
        <FormField label={t('auth.password')} htmlFor="password" required error={errors.password ? t('auth.password') : undefined}>
          <PasswordInput id="password" autoComplete="current-password" invalid={!!errors.password} {...register('password', { required: true, minLength: 8 })} />
        </FormField>
        <div className="flex items-center justify-between">
          <Switch label={t('auth.remember_me')} {...register('remember')} />
          <Link to="/auth/password-reset" className="inline-flex min-h-11 items-center text-sm text-[var(--color-accent)] hover:underline">
            {t('auth.forgot_password')}
          </Link>
        </div>
        <Button type="submit" fullWidth loading={submitting}>{t('auth.sign_in')}</Button>
      </form>
    </div>
  );
}
